import { Platform } from 'react-native';
import { httpsCallable, functions } from '@/lib/functions';
import { auth } from '@/lib/firebase';
import { useUserStore } from '@/state/userStore';
import Constants from 'expo-constants';
import { ensureFreshToken, withAuthRetry } from '@/services/authSessionRecovery';

export type InboxProviderId = "gmail" | "outlook" | "icloud";

export type BookingType =
  | "flight"
  | "hotel"
  | "car"
  | "restaurant"
  | "activity"
  | "transportation"
  | "ferry"
  | "shopping"
  | "other";

export type FlightSegment = {
  origin?: string;
  destination?: string;
  departureDate?: Date;
  arrivalDate?: Date;
  departureTime?: string;
  arrivalTime?: string;
  flightNumber?: string;
  airlineCode?: string;
};

export type ExtractedBooking = {
  type: BookingType;
  provider: string;
  confirmationNumber?: string;
  checkInDate?: Date;
  checkOutDate?: Date;
  departureDate?: Date;
  arrivalDate?: Date;
  startTime?: string;
  endTime?: string;
  location?: string;
  address?: string;
  origin?: string;
  destination?: string;
  flightNumber?: string;
  airlineCode?: string;
  segments?: FlightSegment[];
  amount?: number;
  currency?: string;
  rawEmailSubject?: string;
  rawEmailDate?: Date;
  /**
   * Source system for this booking (e.g., 'email', 'manual', 'amadeus' legacy value).
   * This is stored on the booking document and used by backend sync logic.
   */
  source?: string;
  status?: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  sourceAttachment?: {
    filename: string;
    mimeType: string;
  };
  attachments?: Array<{
    url: string;
    name?: string;
    contentType?: string;
    size?: number;
    source?: string;
  }>;
  confidence?: number;
  labels?: string[];
};

type SyncInboxPayload = {
  tripId: string;
  provider: InboxProviderId;
  autoStore?: boolean; // If false, don't auto-store bookings, return them for user confirmation
};

type SyncInboxResponse = {
  success: boolean;
  provider: InboxProviderId;
  bookingsFound: number;
  bookings: Array<Omit<ExtractedBooking, "checkInDate" | "checkOutDate" | "departureDate" | "arrivalDate" | "rawEmailDate"> & {
    checkInDate?: string;
    checkOutDate?: string;
    departureDate?: string;
    arrivalDate?: string;
    rawEmailDate?: string;
  }>;
  unmatchedBookings?: Array<Omit<ExtractedBooking, "checkInDate" | "checkOutDate" | "departureDate" | "arrivalDate" | "rawEmailDate"> & {
    checkInDate?: string;
    checkOutDate?: string;
    departureDate?: string;
    arrivalDate?: string;
    rawEmailDate?: string;
  }>;
  bookingsForOtherTrips?: Array<{
    booking: Omit<ExtractedBooking, "checkInDate" | "checkOutDate" | "departureDate" | "arrivalDate" | "rawEmailDate"> & {
      checkInDate?: string;
      checkOutDate?: string;
      departureDate?: string;
      arrivalDate?: string;
      rawEmailDate?: string;
    };
    tripId: string;
    score: number;
  }>;
  autoStored?: boolean;
  restaurantReservationsFound?: number;
  activityBookingsFound?: number;
  carRentalsFound?: number;
  travelCompanionsFound?: number;
  preferencesFound?: number;
  requiresAuth?: boolean;
  message?: string;
  error?: string;
};

export type InboxStatus = {
  provider: InboxProviderId;
  connected: boolean;
  requiresAuth?: boolean;
  lastSyncAt?: string;
  lastConnectedAt?: string;
  message?: string;
};

/** Client-side throttle: cuts duplicate status checks when multiple screens mount together. */
export type CheckInboxStatusOptions = {
  /** Skip read-through cache and do not write results (use for OAuth polling / post-auth verify). */
  bypassCache?: boolean;
};

const INBOX_STATUS_CACHE_TTL_MS = 120_000;

/** Tiny gap between provider checks within one batch — avoids syncing two tabs into 6 simultaneous RPC bursts. */
const CHECK_INBOX_STATUS_BATCH_GAP_MS = 80;

const inboxStatusCache = new Map<string, { value: InboxStatus; expiresAt: number }>();
const inboxStatusInFlight = new Map<string, Promise<InboxStatus>>();

/** Coalesces concurrent gmail+outlook+icloud checks (same cache mode) across Trips, Wallet, etc. */
const checkAllConnectionsInFlight = new Map<
  string,
  Promise<Record<InboxProviderId, boolean>>
>();

const ORDERED_INBOX_PROVIDERS: InboxProviderId[] = ["gmail", "outlook", "icloud"];
const getCurrentUid = (): string | undefined => {
  return useUserStore.getState().firebaseUser?.uid ?? auth.currentUser?.uid ?? undefined;
};

function inboxStatusCacheKey(provider: InboxProviderId): string | null {
  const uid = getCurrentUid();
  return uid ? `${uid}:${provider}` : null;
}

function isInboxStatusRateLimited(error: unknown): boolean {
  const maybe = (error ?? {}) as { code?: string; message?: string; status?: number };
  const code = String(maybe.code ?? '').toLowerCase();
  const message = String(maybe.message ?? '').toLowerCase();
  return (
    maybe.status === 429 ||
    code.includes('resource-exhausted') ||
    message.includes('429') ||
    message.includes('rate limit') ||
    message.includes('too many')
  );
}

/**
 * Drop cached inbox status for the signed-in user (one provider or all).
 * Call after disconnect; successful OAuth resume also refreshes cache via the hook.
 */
export function invalidateInboxStatusCache(provider?: InboxProviderId): void {
  const uid = getCurrentUid();
  if (!uid) {
    inboxStatusCache.clear();
    checkAllConnectionsInFlight.clear();
    return;
  }
  if (provider) {
    inboxStatusCache.delete(`${uid}:${provider}`);
    return;
  }
  const prefix = `${uid}:`;
  for (const key of inboxStatusCache.keys()) {
    if (key.startsWith(prefix)) {
      inboxStatusCache.delete(key);
    }
  }
  checkAllConnectionsInFlight.delete(`${uid}:cached`);
  checkAllConnectionsInFlight.delete(`${uid}:fresh`);
}

type SaveImapSettingsPayload = {
  host: string;
  username: string;
  password: string;
  port?: number;
  secure?: boolean;
  mailbox?: string;
  providerId?: InboxProviderId;
};

const callSyncInbox = httpsCallable<SyncInboxPayload, SyncInboxResponse>(
  functions,
  "syncInbox",
);

const callCheckInboxStatus = httpsCallable<
  {provider: InboxProviderId},
  InboxStatus
>(functions, "checkInboxStatus");

const callInitiateInboxAuth = httpsCallable<
  { provider: InboxProviderId; oauthSurface?: 'web' | 'native' },
  { authUrl?: string; message?: string }
>(functions, "initiateInboxAuth");

const callSaveImapSettings = httpsCallable<
  SaveImapSettingsPayload,
  {success: boolean}
>(functions, "saveImapSettings");

const callDisconnectEmailProvider = httpsCallable<
  {provider: InboxProviderId},
  {success: boolean}
>(functions, "disconnectEmailProvider");

const parseDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const mapSegments = (
  segments?: Array<{
    origin?: string;
    destination?: string;
    departureDate?: string;
    arrivalDate?: string;
    departureTime?: string;
    arrivalTime?: string;
    flightNumber?: string;
    airlineCode?: string;
  }>,
): FlightSegment[] | undefined => {
  if (!segments || segments.length === 0) return undefined;
  const mapped = segments.map((s) => ({
    origin: s.origin,
    destination: s.destination,
    departureDate: parseDate(s.departureDate),
    arrivalDate: parseDate(s.arrivalDate),
    departureTime: s.departureTime,
    arrivalTime: s.arrivalTime,
    flightNumber: s.flightNumber,
    airlineCode: s.airlineCode,
  }));
  return mapped.length ? mapped : undefined;
};

const mapBookings = (
  response: SyncInboxResponse,
): ExtractedBooking[] => (response.bookings || []).map((booking) => ({
  ...booking,
  checkInDate: parseDate(booking.checkInDate),
  checkOutDate: parseDate(booking.checkOutDate),
  departureDate: parseDate(booking.departureDate),
  arrivalDate: parseDate(booking.arrivalDate),
  rawEmailDate: parseDate(booking.rawEmailDate),
  segments: mapSegments((booking as any).segments),
}));

/**
 * Backwards-compatible wrapper used across the app.
 * Delegates to syncInboxBookings with the same signature.
 */
export const syncInbox = (
  tripId: string,
  provider: InboxProviderId,
  options?: { autoStore?: boolean },
) => syncInboxBookings(tripId, provider, options);

export const syncInboxBookings = async (
  tripId: string,
  provider: InboxProviderId,
  options?: { autoStore?: boolean },
): Promise<{
  status: "ok" | "auth" | "error";
  data?: ExtractedBooking[];
  unmatchedBookings?: ExtractedBooking[];
  bookingsForOtherTrips?: Array<{ booking: ExtractedBooking; tripId: string; score: number }>;
  autoStored?: boolean;
  message?: string;
}> => {
  try {
    console.log(`[InboxSync] Starting sync for provider ${provider}`);
    const response = await withAuthRetry(() =>
      callSyncInbox({ tripId, provider, autoStore: options?.autoStore ?? true })
    );
    const payload = response.data;

    console.log('[InboxSync] Sync response received', {
      success: payload.success,
      requiresAuth: payload.requiresAuth,
      bookingsFound: payload.bookingsFound,
    });

    if (payload.requiresAuth) {
      console.warn(`[InboxSync] Authentication required for provider ${provider}`);
      return {status: "auth", message: payload.message};
    }

    if (!payload.success) {
      const errorMessage = payload.error || "Sync failed";
      console.error(`[InboxSync] Sync failed for provider ${provider}`, { message: errorMessage });
      return {status: "error", message: errorMessage};
    }

    console.log(`[InboxSync] Sync successful: found ${payload.bookingsFound} bookings`);
    return {
      status: "ok",
      data: mapBookings(payload),
      unmatchedBookings: payload.unmatchedBookings ? mapBookings({ ...payload, bookings: payload.unmatchedBookings }) : undefined,
      bookingsForOtherTrips: payload.bookingsForOtherTrips?.map(b => ({
        booking: mapBookings({ ...payload, bookings: [b.booking] })[0],
        tripId: b.tripId,
        score: b.score,
      })),
      autoStored: payload.autoStored ?? true,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[InboxSync] Exception during sync for provider ${provider}`, { message });
    const errorMessage = error instanceof Error ? error.message : "Failed to sync inbox";
    return {status: "error", message: errorMessage};
  }
};

/**
 * Check if we're running in emulator mode
 */
const isEmulatorMode = (): boolean => {
  const useFunctionsEmulatorConfig = Constants.expoConfig?.extra?.useFunctionsEmulator;
  const useEmulatorFromConfig = useFunctionsEmulatorConfig === true;
  const useEmulatorFromEnv = useFunctionsEmulatorConfig === undefined && process.env.EXPO_PUBLIC_USE_FUNCTIONS_EMULATOR === 'true';
  return __DEV__ && (useEmulatorFromConfig || useEmulatorFromEnv);
};

// Exported for screens that need to adjust UX (polling/backoff) when emulators are enabled.
export const isInboxEmulatorMode = isEmulatorMode;

async function checkInboxStatusUncached(provider: InboxProviderId): Promise<InboxStatus> {
  if (isEmulatorMode()) {
    try {
      console.log(`[InboxSync] Using production HTTP function for checkInboxStatus (emulator mode)`);

      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const idToken = await ensureFreshToken(false);

      const productionFunctionUrl = `https://us-central1-tripflow1-fb52f.cloudfunctions.net/checkInboxStatusProduction?provider=${provider}`;

      const response = await fetch(productionFunctionUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[InboxSync] Production status response received');
      return data;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[InboxSync] Error calling production function, falling back', { message });
      const response = await withAuthRetry(() => callCheckInboxStatus({ provider }));
      return response.data;
    }
  }

  const response = await withAuthRetry(() => callCheckInboxStatus({ provider }));
  return response.data;
}

/**
 * Check inbox status - uses production HTTP function in emulator mode
 * to bypass Admin SDK emulator connection issues.
 * Deduplicates concurrent requests and caches briefly to reduce rate-limit hits.
 */
export async function checkInboxStatus(
  provider: InboxProviderId,
  options?: CheckInboxStatusOptions,
): Promise<InboxStatus> {
  const key = inboxStatusCacheKey(provider);

  if (!options?.bypassCache && key) {
    const hit = inboxStatusCache.get(key);
    if (hit && Date.now() < hit.expiresAt) {
      return hit.value;
    }
    const existing = inboxStatusInFlight.get(key);
    if (existing) {
      return existing;
    }
  }

  const pending = checkInboxStatusUncached(provider)
    .then((value) => {
      if (key && !options?.bypassCache) {
        inboxStatusCache.set(key, {
          value,
          expiresAt: Date.now() + INBOX_STATUS_CACHE_TTL_MS,
        });
      }
      return value;
    })
    .catch((error) => {
      if (key && isInboxStatusRateLimited(error)) {
        const cached = inboxStatusCache.get(key);
        if (cached) {
          return cached.value;
        }
      }
      throw error;
    })
    .finally(() => {
      if (key && !options?.bypassCache) {
        inboxStatusInFlight.delete(key);
      }
    });

  if (key && !options?.bypassCache) {
    inboxStatusInFlight.set(key, pending);
  }

  return pending;
}

/**
 * Fetches gmail/outlook/iCloud connected flags once, coalescing concurrent callers
 * (same uid + bypassCache) into a single sequential chain to reduce inbox rate-limit hits.
 *
 * @internal Prefer useInboxConnectionsStore / useInboxConnections hook so refresh policy
 * stays centralized instead of importing this from screens.
 */
export async function checkAllInboxProviderConnections(
  options?: CheckInboxStatusOptions,
): Promise<Record<InboxProviderId, boolean>> {
  const uid = getCurrentUid();
  const nextEmpty = (): Record<InboxProviderId, boolean> => ({
    gmail: false,
    outlook: false,
    icloud: false,
  });
  if (!uid) {
    return nextEmpty();
  }

  const key = `${uid}:${options?.bypassCache ? "fresh" : "cached"}`;
  const existing = checkAllConnectionsInFlight.get(key);
  if (existing) {
    return existing;
  }

  const pending = (async () => {
    const out = nextEmpty();
    for (let i = 0; i < ORDERED_INBOX_PROVIDERS.length; i++) {
      const provider = ORDERED_INBOX_PROVIDERS[i];
      try {
        const status = await checkInboxStatus(provider, options);
        out[provider] = Boolean(status?.connected);
      } catch (error) {
        const cached = inboxStatusCache.get(`${uid}:${provider}`);
        if (cached && isInboxStatusRateLimited(error)) {
          out[provider] = Boolean(cached.value.connected);
        } else {
          out[provider] = false;
        }
      }
      if (i < ORDERED_INBOX_PROVIDERS.length - 1 && CHECK_INBOX_STATUS_BATCH_GAP_MS > 0) {
        await new Promise((r) => setTimeout(r, CHECK_INBOX_STATUS_BATCH_GAP_MS));
      }
    }
    return out;
  })().finally(() => {
    checkAllConnectionsInFlight.delete(key);
  });

  checkAllConnectionsInFlight.set(key, pending);
  return pending;
}

export const initiateInboxAuth = async (
  provider: InboxProviderId,
): Promise<{authUrl?: string; message?: string}> => {
  const oauthSurface = Platform.OS === 'web' ? 'web' : 'native';
  const response = await withAuthRetry(() =>
    callInitiateInboxAuth({ provider, oauthSurface }),
  );
  return response.data;
};

export const saveImapSettings = async (
  settings: SaveImapSettingsPayload,
): Promise<boolean> => {
  const response = await withAuthRetry(() => callSaveImapSettings(settings));
  return Boolean(response.data?.success);
};

export const disconnectEmailProvider = async (
  provider: InboxProviderId,
): Promise<boolean> => {
  const response = await withAuthRetry(() => callDisconnectEmailProvider({ provider }));
  const ok = Boolean(response.data?.success);
  if (ok) {
    invalidateInboxStatusCache(provider);
  }
  return ok;
};


